import { useQuery } from '@tanstack/react-query'
import { Bot } from 'lucide-react'
import { fetchAiInteractions, fetchAiTurns } from '@/api/client'
import { ApiError } from '@/api/client'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { LoadingState } from '@/components/LoadingState'
import { formatDateTime } from '@/lib/format-date'
import { AdminSection } from '@/features/admin/AdminSection'

export function AiLogAdminPage() {
  const turnsQuery = useQuery({
    queryKey: ['ai', 'turns'],
    queryFn: () => fetchAiTurns(100),
  })
  const interactionsQuery = useQuery({
    queryKey: ['ai', 'interactions'],
    queryFn: () => fetchAiInteractions(100),
  })

  const items = turnsQuery.data ?? []
  const interactions = interactionsQuery.data ?? []

  return (
    <AdminSection
      title="AI turn log"
      tone="indigo"
      icon={Bot}
      lede="Role-scoped AI turns and model interactions (BR-49 / S-2A-019). Raw model payloads stay in secured store pending CR-NFR-09."
    >
      {turnsQuery.isLoading && (
        <LoadingState label="Loading AI turns…" variant="skeleton-table" rows={8} columns={5} />
      )}

      {turnsQuery.error && (
        <ErrorState
          message={
            turnsQuery.error instanceof ApiError
              ? turnsQuery.error.message
              : 'Could not load AI turns.'
          }
          onRetry={() => void turnsQuery.refetch()}
        />
      )}

      {!turnsQuery.isLoading && !turnsQuery.error && items.length === 0 && (
        <EmptyState title="No AI turns recorded yet." />
      )}

      {!turnsQuery.isLoading && items.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th>When</th>
                <th>Channel</th>
                <th>Kind</th>
                <th>Outcome</th>
                <th>Turn id</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td>{row.channel}</td>
                  <td>{row.kind}</td>
                  <td>{row.outcome ?? '—'}</td>
                  <td>
                    <code>{row.id.slice(0, 8)}…</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mt-8 text-sm font-semibold">Model interactions</h3>
      {interactionsQuery.isLoading && (
        <LoadingState label="Loading interactions…" variant="skeleton-table" rows={6} columns={5} />
      )}
      {interactionsQuery.error && (
        <ErrorState
          message={
            interactionsQuery.error instanceof ApiError
              ? interactionsQuery.error.message
              : 'Could not load AI interactions.'
          }
          onRetry={() => void interactionsQuery.refetch()}
        />
      )}
      {!interactionsQuery.isLoading && !interactionsQuery.error && interactions.length === 0 && (
        <EmptyState title="No model interactions recorded yet." />
      )}
      {!interactionsQuery.isLoading && interactions.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th>When</th>
                <th>Deployment</th>
                <th>Latency</th>
                <th>Failure</th>
                <th>Turn</th>
              </tr>
            </thead>
            <tbody>
              {interactions.map((row) => {
                const deployment = row.model_deployment
                const freeform = deployment === 'ask_freeform' || deployment.startsWith('tool:')
                return (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.created_at)}</td>
                    <td>
                      {deployment}
                      {freeform ? (
                        <span
                          className="flag-chip flag-chip--info"
                          style={{ marginLeft: '0.35rem' }}
                        >
                          freeform
                        </span>
                      ) : null}
                    </td>
                    <td>{row.latency_ms != null ? `${row.latency_ms} ms` : '—'}</td>
                    <td>{row.failure_code ?? '—'}</td>
                    <td>
                      <code>{row.ai_turn_id.slice(0, 8)}…</code>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  )
}
